import BluebirdPromise from 'bluebird'
import { type Product } from 'legacy/routes/catalog/types/product.types'
import { fetchMeshProductInfo } from './fetchMeshProductInfo'
import { fetchMeshAisleProducts } from './fetchMeshAisleProducts'
import { fetchMeshAisleItems } from './fetchMeshAisleItems'
import {
  prepareProductVariables,
  prepareItemVariables,
} from 'legacy/routes/catalog/departments/utils/prepareVariables'
import { type FetchAisleProductsWithLocaleParams } from 'legacy/routes/catalog/departments/utils/types'
import { normalizeProductCodes } from 'utils/product-codes/normalizeProductCodes'
import {
  type FetchDepartmentsAndAislesItemsMeshQuery,
  type FetchDepartmentsAndAislesProductsMeshQuery,
} from '__codegen__/api'

type MeshResponse =
  | FetchDepartmentsAndAislesProductsMeshQuery['retailerToolsServiceGetRetailerProducts']
  | FetchDepartmentsAndAislesItemsMeshQuery['retailerToolsServiceGetItems']

type MeshResult = MeshResponse['results'][0]
/**
 * A combined query, which will obtain products, and later fetch their information, combine
 * them together, and normalize them to the expected structure.
 */
export const fetchMeshAisleProductsWithInfo = (...args: FetchAisleProductsWithLocaleParams) => {
  let productsPromise: () => Promise<MeshResponse>
  const [localeCode, params, query] = args

  if (query?.inventory_area_ids) {
    productsPromise = () => fetchMeshAisleItems(prepareItemVariables(...args))
  } else {
    productsPromise = () => fetchMeshAisleProducts(prepareProductVariables(...args))
  }

  const promise = productsPromise()
    .then(async ({ results, ...rest }) => {
      if (!results?.length) return { results: [], totalCount: '0' }

      const retailerId = params.warehouse_id
      const info = await fetchMeshProductInfo({
        retailerId,
        productIds: (results as Array<MeshResult>).map(p => p.productId),
      })

      const normalizedResults: Pick<
        Product,
        'image_url' | 'display_name' | 'id' | 'product_codes' | 'available_items_count'
      >[] = (results as Array<MeshResult>).map(product => ({
        image_url: product.primaryImageUrl,
        display_name: product.productDisplayName,
        id: parseInt(product.productId, 10),
        product_codes: normalizeProductCodes(product.productCodes || []),
        available_items_count: parseInt(info[product.productId]?.availableItemsCount ?? '0', 10),
      }))

      return { ...rest, results: normalizedResults }
    })
    .then(data => ({
      data: data.results,
      meta: {
        per_page: 10,
        total_pages: Math.ceil(parseInt(data.totalCount, 10) / 10),
      },
    }))

  /**
   * This must return a bluebird promise. This is due to the consumer of these promises
   * expecting some functions that are not part of the promise standard.
   *
   * Note that this function cannot be async, as it will wrap the promise in
   * a standard promise.
   */
  return BluebirdPromise.resolve(promise)
}
